import { useMutation } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { computeMpsSyncPlan, lookupHashesOf } from '@/lib/api/med-requests/mps-sync-plan'
import http from '@/lib/http'
import { type ExtraTreatment, type RemovedTreatment, useMedicationRequestStore } from '@/lib/stores/medication-request'
import { queryClient } from '@/services/queryClient'
import type { MedRequest } from '@/types/data'

/**
 * Mutations over a multiproduct (MPS) subscription's medications — see `isMpsMedRequest`.
 * `subscriptionHash` is the hash of the subscription order itself (`evaroType: 'mps'`), not of a
 * medication order. The API rejects non-MPS subscriptions, the placeholder product, medications
 * already on the subscription (add, swap) and item hashes of another subscription (swap, remove)
 * with a 400 carrying PHP's code/message.
 */

export type AddMedicationToSubscriptionResponse = {
  subscriptionHash: string
  productHash: string
  orderHash: string
  orderNumber: string
  /** Id of the medication request created for the added medication (its order item hash). */
  medReqId: string
}

export type SwapSubscriptionMedicationsResponse = {
  subscriptionHash: string
  /** Every medication order touched by the swap. */
  orders: Array<{ orderHash: string; orderNumber: string }>
}

export type RemoveSubscriptionMedicationResponse = {
  subscriptionHash: string
  itemHash: string
  /** Null when no order was touched — already with the pharmacy, or never reached an order. */
  orderHash: string | null
  orderNumber: string | null
}

// The `http` interceptor already toasts the API's own message (PHP's rejection reason), so the
// per-mutation toast stays generic, as in `useMedRequestAction`.
const useSubscriptionMutationToast = (description: string) => {
  const { toast } = useToast()

  return () => toast({ title: 'Error', description })
}

export const useAddMedicationToSubscription = () => {
  const onError = useSubscriptionMutationToast('Error adding the medication to the subscription')

  return useMutation({
    mutationKey: ['addMedicationToSubscription'],
    mutationFn: ({
      subscriptionHash,
      productHash
    }: {
      subscriptionHash: string
      /** these can also be variantHashes */
      productHash: string
    }): Promise<AddMedicationToSubscriptionResponse> =>
      http.post(`/prescriber/subscriptions/multiproduct/${subscriptionHash}/medications/${productHash}`),
    onError
  })
}

export const useSwapSubscriptionMedications = () => {
  const onError = useSubscriptionMutationToast('Error replacing the medication on the subscription')

  return useMutation({
    mutationKey: ['swapSubscriptionMedications'],
    mutationFn: ({
      subscriptionHash,
      swaps
    }: {
      subscriptionHash: string
      swaps: Array<{ itemHash: string; newProductHash: string }>
    }): Promise<SwapSubscriptionMedicationsResponse> =>
      http.post(`/prescriber/subscriptions/multiproduct/${subscriptionHash}/medications/swap`, { swaps }),
    onError
  })
}

export const useRemoveSubscriptionMedication = () => {
  const onError = useSubscriptionMutationToast('Error removing the medication from the subscription')

  return useMutation({
    mutationKey: ['removeSubscriptionMedication'],
    mutationFn: ({
      subscriptionHash,
      itemHash
    }: {
      subscriptionHash: string
      itemHash: string
    }): Promise<RemoveSubscriptionMedicationResponse> =>
      http.delete(`/prescriber/subscriptions/multiproduct/${subscriptionHash}/medications/${itemHash}`),
    onError
  })
}

export type MpsSyncResult = {
  /** False when a call failed — approve must abort so the prescriber can retry. */
  ok: boolean
  /**
   * Why the sync stopped, for approve's toast: PHP's rejection reason, or the sync's own. Approve
   * reports it rather than the sync toasting it, as TOAST_LIMIT is 1 and approve's toast would
   * replace any the sync raised — PHP's reason included.
   */
  error?: string
  /** True once a removal, add or swap landed on the subscription. */
  hasSavedChanges: boolean
}

/**
 * PHP's rejection reason off a failed subscription mutation. The `http` interceptor rejects with the
 * response body, which NodeAPI's error handler shapes as `{ error: { code, message } }` for the
 * forwarded 4xx; anything else has no reason worth showing.
 */
export const subscriptionErrorMessage = (error: unknown): string | undefined => {
  const message = (error as { error?: { message?: unknown } } | null)?.error?.message
  return typeof message === 'string' && message.trim() ? message.trim() : undefined
}

/** The medication an unresolved plan entry stands for: a changed card's baseline, a queued removal's own. */
const baselineOf = (entry: ExtraTreatment | RemovedTreatment) =>
  'id' in entry ? entry.persistedMedication : entry.medication

/**
 * Applies the treatment diffs accumulated on the prescription details to the MPS subscription.
 * Called by useApproveMedicationRequest right before the approve action fires — nothing on the
 * subscription changes until the prescriber approves; until then every pick, change and removal
 * is local-only.
 *
 * What to apply is computed up front by `computeMpsSyncPlan` (hash-based per-card diffs; an
 * unchanged card produces no request) and executed in plan order: queued removals first (so a
 * product removed and re-picked in the same session doesn't trip the API's duplicate reject),
 * then adds, then one batched swap. A plan with unresolved baselines aborts before any call.
 *
 * `ok` is false when a call fails, so approve can abort and the prescriber can retry. Changes
 * that do land update their card's baseline (`persistedMedication`) or leave the removal queue,
 * so a retry after a partial failure only processes what's still outstanding — the API 400s on
 * duplicates.
 */
export const useSyncMpsTreatmentsOnApprove = () => {
  const addMedicationMutation = useAddMedicationToSubscription()
  const swapMedicationsMutation = useSwapSubscriptionMedications()
  const removeMedicationMutation = useRemoveSubscriptionMedication()

  return async (medRequest: MedRequest): Promise<MpsSyncResult> => {
    let hasSavedChanges = false
    const done = (ok: boolean, error?: string): MpsSyncResult => ({ ok, error, hasSavedChanges })

    // The reason of the call that failed, captured by `failed` for `abort`.
    let failure: string | undefined
    const failed = (error: unknown) => {
      failure = subscriptionErrorMessage(error)
      return null
    }

    const {
      medication,
      extraTreatments,
      removedTreatments,
      updateExtraTreatment,
      unqueueRemovedTreatment,
      updateDroppedProductHashes
    } = useMedicationRequestStore.getState()
    const refresh = () => queryClient.invalidateQueries({ queryKey: ['medicationRequestById', medRequest.id] })

    // An 'mps' row is an item of the subscription order itself, so its own orderHash IS the
    // subscription hash; on a medication order the API resolves and attaches `subscriptionHash`.
    const subscriptionHash =
      medRequest.evaroType?.toLowerCase() === 'mps' ? medRequest.orderHash : medRequest.subscriptionHash

    if (!subscriptionHash) {
      return done(false, 'Could not resolve the subscription for this request')
    }

    const plan = computeMpsSyncPlan(medRequest, { medication, extraTreatments, removedTreatments })

    if (plan.unresolvedBaselines.length || plan.unresolvedPrimarySwap) {
      const unresolvedNames = new Set(
        [
          plan.unresolvedPrimarySwap ? medRequest.orderedItem?.name : null,
          ...plan.unresolvedBaselines.map(entry => baselineOf(entry)?.productName)
        ].filter(Boolean)
      )
      // Quoted: names carry commas of their own ("Evorel 50, 8 Patches").
      const missing = [...unresolvedNames].map(name => `"${name}"`).join(' and ') || 'the medication'

      return done(false, `Could not find ${missing} on the subscription`)
    }

    // Already off the subscription — nothing to call, just drop from the queue.
    for (const removed of plan.settledRemovals) {
      unqueueRemovedTreatment(removed)
    }

    const abort = () => {
      if (hasSavedChanges) refresh()
      return done(false, failure)
    }

    for (const { itemHash, removed } of plan.removals) {
      const result = await removeMedicationMutation.mutateAsync({ subscriptionHash, itemHash }).catch(failed)

      if (!result) return abort()

      hasSavedChanges = true
      unqueueRemovedTreatment(removed)
      updateDroppedProductHashes({ dropped: lookupHashesOf(removed.medication) })
    }

    for (const addition of plan.additions) {
      const added = await addMedicationMutation
        .mutateAsync({ subscriptionHash, productHash: addition.catalogueHash })
        .catch(failed)

      if (!added) return abort()

      hasSavedChanges = true
      if (addition.treatmentId) {
        // The response has no subscription item hash, but the created medication order's hash
        // pins this card to its family row for later item resolution (see resolveBaselineItemHash),
        // and its med request id lets approve wait until the family lists the created row.
        updateExtraTreatment(addition.treatmentId, {
          persistedMedication: addition.medication,
          persistedOrderHash: added.orderHash,
          persistedMedReqId: added.medReqId || null
        })
      }
    }

    if (plan.swaps.length) {
      const swapped = await swapMedicationsMutation
        .mutateAsync({
          subscriptionHash,
          swaps: plan.swaps.map(({ itemHash, newCatalogueHash }) => ({ itemHash, newProductHash: newCatalogueHash }))
        })
        .catch(failed)

      if (!swapped) return abort()

      hasSavedChanges = true
      updateDroppedProductHashes({
        dropped: plan.swaps.flatMap(swap => swap.replacedHashes),
        restored: plan.swaps.flatMap(swap => lookupHashesOf(swap.medication))
      })
      for (const swap of plan.swaps) {
        if (swap.treatmentId) updateExtraTreatment(swap.treatmentId, { persistedMedication: swap.medication })
      }
    }

    if (hasSavedChanges) refresh()
    return done(true)
  }
}
